import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraDashboardPage } from '@mono/ui-components/admirals';
import { SpecraRecordRail } from '@mono/ui-components/captains';
import { SpecraSpinner } from '@mono/ui-components/ensigns';
import { Testids } from '@mono/ui-selectors';
import { Home } from 'lucide-react';
import { useEffect } from 'react';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { ErrorState } from '../Delivery/_shared/components/PageStates';
import { RecordUnavailableState } from './RecordUnavailableState';
/**
 * Generated test ids for the rail sections registry record pages render
 * (keyed by `SpecraRecordRailSection.id`). A section id without an entry
 * renders no test id rather than an ungenerated literal (R10).
 */ const RAIL_SECTION_TESTIDS = {
    'who-knows': Testids.registryRecord.railWhoKnows,
    'cares-about': Testids.registryRecord.railCaresAbout,
    documents: Testids.registryRecord.railDocuments
};
/**
 * The one frame every registry record page renders in (REG-08): the global
 * sidebar shell's breadcrumbs (Home / Registries / <Kind> / <Name>), a page
 * header, the work column and the record rail. Loading, error and unavailable
 * states replace the whole page; unavailable never shows the id or a name.
 */ export function RegistryRecordFrame({ kindLabel, tab, title, status, error, onRetry, header, railSections, railLabel, children }) {
    // Root-level route outside DeliveryLayout: suppress the legacy metadata band
    // and restore it on the way out (same as RegistriesPage).
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    // An unavailable record shows no name in the trail either.
    const crumbTitle = status === 'unavailable' ? undefined : title;
    const setBreadcrumbItems = useLayoutStore((s)=>s.setBreadcrumbItems);
    useEffect(()=>{
        setBreadcrumbItems([
            {
                label: 'Home',
                href: '/',
                icon: /*#__PURE__*/ _jsx(Home, {
                    className: "h-3 w-3"
                })
            },
            {
                label: 'Registries',
                href: '/registries'
            },
            {
                label: kindLabel,
                href: `/registries?tab=${tab}`
            },
            {
                label: crumbTitle ?? '…'
            }
        ]);
        return ()=>setBreadcrumbItems(null);
    }, [
        kindLabel,
        tab,
        crumbTitle,
        setBreadcrumbItems
    ]);
    if (status === 'loading') {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex justify-center py-16",
            "data-testid": Testids.registryRecord.frame,
            children: /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading"
            })
        });
    }
    if (status === 'error') {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6",
            "data-testid": Testids.registryRecord.frame,
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: error,
                title: "This record could not be loaded",
                onRetry: onRetry
            })
        });
    }
    if (status === 'unavailable') {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6",
            "data-testid": Testids.registryRecord.frame,
            children: /*#__PURE__*/ _jsx(RecordUnavailableState, {
                tab: tab
            })
        });
    }
    return /*#__PURE__*/ _jsx(SpecraDashboardPage, {
        "data-testid": Testids.registryRecord.frame,
        padding: "page",
        railVariant: "record",
        railLabel: railLabel ?? `${title ?? kindLabel} details`,
        header: /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.registryRecord.header,
            children: header
        }),
        rail: railSections && railSections.length > 0 ? /*#__PURE__*/ _jsx(SpecraRecordRail, {
            "data-testid": Testids.registryRecord.rail,
            sections: railSections,
            getSectionTestId: (s)=>RAIL_SECTION_TESTIDS[s.id]
        }) : undefined,
        children: children
    });
}
